import { computed } from "vue";
import { useBetData } from "./useBetData";
import MarketSupports from "../lib/marketSupports";
import {
  HANDICAP_BETTING,
  MATCH_WINNER,
  OVER_UNDER_TOTALS,
  FTR_GROUP,
  LIST_GROUP,
  PROMO_GROUP,
  MAIN_MARKET_GROUP
} from "../constants/market-types";
interface EventMarketGroups {
  [PROMO_GROUP]?: string[];
  [FTR_GROUP]?: string[];
  [LIST_GROUP]?: string[];
  [MAIN_MARKET_GROUP]?: string[];
}

const MAIN_MARKET_SPORTS = [
  "Basketball",
  "Baseball",
  "American Football",
  "Ice Hockey"
];

export function useDisplayEvents() {
  const { events, markets } = useBetData();
  const marketSupports = MarketSupports();

  /**
   * Method to group all the marketIds for an event by their normalised market type.
   * @param {string[]} marketIds
   */
  const getMarketsGroupedByType = (marketIds: string[]): EventMarketGroups => {
    return marketIds.reduce((groupAccumulator, currentID) => {
      const marketType = markets.value[currentID].marketType;
      const marketTypeGroup = marketSupports[marketType];

      return {
        ...groupAccumulator,
        [marketTypeGroup]: groupAccumulator[marketTypeGroup]
          ? [...groupAccumulator[marketTypeGroup], currentID]
          : [currentID]
      };
    }, {});
  };

  /**
   * method to pull our the market ids that only match the main market types from the FTR market type group.
   * @param {string[]} ftrMarkets
   */
  const getMainMarkets = (
    ftrMarkets: string[] | undefined,
    sport: string
  ): string[] => {
    if (!ftrMarkets || !MAIN_MARKET_SPORTS.includes(sport)) {
      return [];
    }
    const mainMarkets = ftrMarkets.reduce(
      (mainMarketAccumulator, currentID) => {
        const currentMarketType = markets.value[currentID].marketType;
        // only map current market type id if an id hasn't been mapped already for that market type
        if (mainMarketAccumulator[currentMarketType] === "") {
          return {
            ...mainMarketAccumulator,
            [currentMarketType]: currentID
          };
        }
        return mainMarketAccumulator;
      },
      {
        [HANDICAP_BETTING]: "",
        [MATCH_WINNER]: "",
        [OVER_UNDER_TOTALS]: ""
      }
    );
    // removing markets that haven't got ids.
    return Object.values(mainMarkets).filter(market => market !== "");
  };

  /**
   * Mapping the event markets into their groups first to reduce repeated
   * operations of mapping markets.
   */
  const eventsWithGroupedMarkets = computed(() => {
    return (
      events.value &&
      Object.values(events.value).map(evnt => {
        // group event markets by card type
        const marketTypeGroups = getMarketsGroupedByType(evnt.markets);

        // use only 1 promo and list market
        marketTypeGroups[PROMO_GROUP]?.splice(1);
        marketTypeGroups[LIST_GROUP]?.splice(1);

        // collate only the main market types
        const mainMarkets = getMainMarkets(
          marketTypeGroups[FTR_GROUP],
          evnt.sport
        );

        // Display Main market card if more than 1 main market type exists.
        if (mainMarkets.length > 1) {
          marketTypeGroups[MAIN_MARKET_GROUP] = mainMarkets;
          marketTypeGroups[FTR_GROUP] = undefined;
        } else {
          marketTypeGroups[FTR_GROUP]?.splice(1);
        }

        return {
          ...evnt,
          markets: marketTypeGroups
        };
      })
    );
  });

  /**
   * Sorts the event by date order.
   * if the events have matching fixture start time, then the events are ordered by which contains a promo first.
   */
  const eventsOrderbyDate = computed(
    () =>
      eventsWithGroupedMarkets.value?.sort((a, b) => {
        if (new Date(a.fixture) < new Date(b.fixture)) {
          return -1;
        }
        if (new Date(a.fixture) > new Date(b.fixture)) {
          return 1;
        }

        if (
          a.markets[PROMO_GROUP]?.length === 1 &&
          !b.markets[PROMO_GROUP]?.length
        ) {
          return -1;
        }

        if (
          !a.markets[PROMO_GROUP]?.length &&
          b.markets[PROMO_GROUP]?.length === 1
        ) {
          return 1;
        }

        return 0;
      }) || []
  );

  /**
   * Ordering the markets for each of the ordered events
   */
  const orderedMarkets = computed(() => {
    return eventsOrderbyDate.value.reduce((eventsAccumulator, currentEvent) => {
      // push the group arrays into the right order.
      const orderedEventsMarkets: string[][] = [];

      if (currentEvent.markets.promo)
        orderedEventsMarkets.push(currentEvent.markets.promo);

      if (currentEvent.markets.mainMarket)
        orderedEventsMarkets.push(currentEvent.markets.mainMarket);

      if (currentEvent.markets.ftr)
        orderedEventsMarkets.push(currentEvent.markets.ftr);

      if (currentEvent.markets.list)
        orderedEventsMarkets.push(currentEvent.markets.list);

      return [...eventsAccumulator, ...orderedEventsMarkets];
    }, [] as string[][]);
  });

  return {
    eventsOrderbyDate,
    orderedMarkets
  };
}
